import { useCallback, useEffect, useMemo, useState } from "react";
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { Button, Divider, Form, Select } from "antd";
import { HttpStatusCode } from "axios";
import { cookies } from "constants/settings.constants";
import Cookies from "js-cookie";
import { login as loginUser, setUser } from "store/features/auth/authSlice";
import styled from "styled-components";
import { checkEmail } from "utils";

import { ReCAPTCHAVersion } from "api/auth";
import useImpersonateMutation from "api/useImpersonateMutation";
import useOrganizations from "api/useOrganization";
import useUsers from "api/useUsers";

import { IMfaRequired, IUser } from "models/User";

import { BaseButton, BaseInput, Heading } from "components/base";
import FieldErrorMessage from "components/base/FieldErrorMessage";
import PasswordInput from "components/base/PasswordInput";
import { IconSpinner } from "components/icons";
import LoginTypeSelector, {
  LOGIN_TYPE_EMAIL,
  LoginType
} from "components/login/LoginTypeSelector";
import { initialUserState, useUserContext } from "components/user/context";
import { updateSettings } from "components/user/context/reducer";

import "./Login.scss";

const reCaptchaV2SiteKey = "6LeaXGEpAAAAAAjCrAWqP9SND1z40218D75V_Xth";

export default function Login() {
  const data = {
    title: "Sign in",
    emailLabel: "Email address",
    passwordLabel: "Your password",
    buttonLabel: "Login"
  };
  const dispatch = useDispatch();
  const [, userDispatch] = useUserContext();
  const [canImpersonate, setCanImpersonate] = useState(false);
  const [loginType, setLoginType] = useState<LoginType>(LOGIN_TYPE_EMAIL);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ssoRedirectError, setSsoRedirectError] = useState("");
  const [errors, setErrors] = useState("");
  const [isRecaptchaError, setIsRecaptchaError] = useState(false);
  const [isBusy, setBusy] = useState(false);
  const history = useHistory();
  const [token, setToken] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [mfa] = useState("");
  const [impersonateRequestId, setImpersonateRequestId] = useState("");
  const [recaptchaV2Token, setRecaptchaV2Token] = useState("");
  const {
    data: organizations,
    isLoading: isOrgLoading,
    refetch: refetchOrg
  } = useOrganizations(token);

  const {
    data: usersInOrg,
    isLoading: isUserLoading,
    refetch: refetchUsers
  } = useUsers(token, selectedOrg);

  const impersonateMutation = useImpersonateMutation(
    (user: IUser) => {
      dispatch(setUser(user));
      history.push("/app");

      // reset the users settings.
      updateSettings(userDispatch, initialUserState.settings);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err: any) => {
      if (err.response?.status === HttpStatusCode.Forbidden) {
        setErrors("The selected user is private and cannot be impersonated.");
      } else if (err.response?.data) {
        setErrors(err.response.data);
      } else {
        setErrors(
          "Unable to connect to backend service. Please try again at a later time."
        );
      }
    }
  );

  function checkForm() {
    if (!checkEmail(email)) {
      setErrors("Please enter a valid email");
    }
  }

  useEffect(() => {
    // Check for email passed from SSO redirect
    const locationState = history.location.state as
      | { email?: string; ssoError?: string }
      | undefined;
    if (locationState?.email) {
      setEmail(locationState.email);
      if (locationState.ssoError) {
        setSsoRedirectError(locationState.ssoError);
        // Clear error after 5 seconds
        setTimeout(() => setSsoRedirectError(""), 5000);
      }
      // Clear the state to prevent re-filling on refresh
      history.replace("/login", {});
      return;
    }

    // Otherwise, check for stored email
    const storedEmail = Cookies.get(cookies.LOGIN_EMAIL);
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [history]);

  useEffect(() => {
    refetchUsers && refetchUsers();
  }, [selectedOrg]);

  function onRecaptchaCheck(value) {
    setRecaptchaV2Token(value);
  }

  const login = useCallback(
    async (event) => {
      event.preventDefault();
      checkForm();
      setErrors("");

      if (!executeRecaptcha) {
        setErrors("reCAPTCHA is loading, try again.");
        return;
      }

      // generating reCAPTCHA token
      const recaptchaV3Token = await executeRecaptcha("TuringRecaptchaAction");

      setBusy(true);

      dispatch(
        loginUser(
          {
            email: email,
            password: password,
            // fallback on v2 if v3 is failing
            token: isRecaptchaError ? recaptchaV2Token : recaptchaV3Token,
            version: isRecaptchaError ? ReCAPTCHAVersion.V2 : ReCAPTCHAVersion.V3
          },
          (data: IMfaRequired | IUser) => {
            setBusy(false);

            if (data.requiresMfa) {
              const mfaResponse = data as IMfaRequired;
              history.push(
                `/mfa-login?token=${mfaResponse.requestId}&method=${mfaResponse.deliveryMethod}`
              );
            } else {
              const authResponse = data as IUser;
              if (data.canImpersonate && authResponse) {
                setCanImpersonate(true);
                setImpersonateRequestId(authResponse.impersonateRequestId);
                setToken(data.jwtToken);
                //give some time for token to update
                setTimeout(() => refetchOrg(), 100);
                return;
              }
              setCanImpersonate(false);
              history.push("/app");
            }
          },
          (error) => {
            // Check for SsoOnly error code
            if (error.response?.data?.errorCode === "SsoOnly") {
              history.push("/sso", {
                email: email,
                ssoError:
                  error.response?.data?.errorMessage || "SSO authentication required."
              });
              return;
            }

            if (error.response?.data?.errorMessage) {
              setErrors(error.response.data.errorMessage);
              if (
                error.response.data.errorMessage ==
                "reCAPTCHA verification failed, please try again."
              ) {
                setIsRecaptchaError(true);
              }
            } else {
              setErrors(
                "Unable to login, please check that the email and password is valid."
              );
              setIsRecaptchaError(false);
            }
            setBusy(false);
          }
        )
      );
    },
    [executeRecaptcha, email, isRecaptchaError, password, recaptchaV2Token]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function impersonate(values: any) {
    impersonateMutation.mutate({
      ...values,
      impersonateRequestId: impersonateRequestId,
      token: token
    });
  }

  const ssoSupported = process.env.REACT_APP_SSO_SUPPORTED === "true";

  const impersonationUserOptions = useMemo(() => {
    return (usersInOrg ?? [])
      .sort((a, b) => {
        const isAdminA = a.role === 2;
        const isAdminB = b.role === 2;

        if (isAdminA !== isAdminB) {
          return isAdminA ? -1 : 1;
        }

        const aName = a.firstName?.trim();
        const bName = b.firstName?.trim();
        const hasAName = Boolean(aName);
        const hasBName = Boolean(bName);

        if (hasAName !== hasBName) {
          return hasAName ? -1 : 1;
        }

        if (!hasAName && !hasBName) {
          return 0;
        }

        return aName?.localeCompare(bName, undefined, {
          sensitivity: "base"
        });
      })
      .map((user) => {
        const prefix = user.canBeImpersonated ? "" : "*";
        const role =
          user.role === 1
            ? "Standard"
            : user.role === 2
            ? "Admin"
            : user.role === 4
            ? "Power User"
            : "unknown";
        const displayName =
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : "unknown";
        return {
          label: `${prefix}${displayName} - ${role} - (${user.email})`,
          value: user.id
        };
      });
  }, [usersInOrg]);

  if (canImpersonate && (isOrgLoading || isUserLoading)) {
    return <div>Loading...</div>;
  }

  return (
    <div className="content-container">
      <div>
        <Heading className="align-center large" element="h3">
          {data.title}
        </Heading>

        {!canImpersonate && ssoSupported && (
          <LoginTypeSelector value={loginType} onChange={setLoginType} />
        )}

        {!canImpersonate && (
          <>
            {ssoRedirectError && (
              <div
                className="flex items-center justify-center"
                style={{ marginTop: "1rem" }}>
                <FieldErrorMessage message={ssoRedirectError} />
              </div>
            )}

            <div className="flex items-center justify-center">
              {errors.length > 0 && <FieldErrorMessage message={errors} />}
            </div>

            <div className="loginTransitionContainer emailHeight">
              <form name="login" className="login-form" onSubmit={login}>
                <fieldset className="fieldset">
                  <BaseInput
                    type="email"
                    value={email}
                    label={data.emailLabel}
                    required
                    onChange={setEmail}
                    placeholder={"email@gmail.com"}
                    autoComplete="username"
                    id="email-login-input"
                  />
                  <PasswordInput
                    value={password}
                    label={data.passwordLabel}
                    onChange={setPassword}
                    actionButton={"Forgot Password?"}
                    onActionClick={() => {
                      history.push("/forgot-password");
                    }}
                    required
                    id="password-login-input"
                  />
                  <div
                    className="flex items-center justify-center"
                    style={{ marginTop: "3rem" }}>
                    <BaseButton
                      type="submit"
                      appearance="primary"
                      className="login-btn"
                      data-testid="login-submit-button">
                      {data.buttonLabel}
                    </BaseButton>
                  </div>
                </fieldset>
              </form>
            </div>

            {isRecaptchaError && (
              <div className="loginRecaptchaContainer">
                <ReCAPTCHA sitekey={reCaptchaV2SiteKey} onChange={onRecaptchaCheck} />
              </div>
            )}
          </>
        )}

        {canImpersonate && (
          <ImpersonateContainer>
            <Button type="primary" onClick={() => history.push("/app")}>
              Login as Myself
            </Button>
            <Divider />
            <ImpersonateSelector>
              <h5>Impersonate</h5>
              <Form layout="vertical" onFinish={impersonate}>
                <Form.Item name="organizationId" label="Organization">
                  <Select
                    showSearch
                    loading={isOrgLoading}
                    optionFilterProp="label"
                    onChange={(val) => {
                      setSelectedOrg(val);
                    }}
                    options={organizations.map((org) => {
                      return { label: org.name, value: org.id };
                    })}></Select>
                </Form.Item>
                <Form.Item name="userId" label="User">
                  <Select
                    showSearch
                    loading={isUserLoading}
                    optionFilterProp="label"
                    options={impersonationUserOptions}></Select>
                </Form.Item>
                <Form.Item
                  name="mfa"
                  label="MFA Code"
                  rules={[
                    { required: true, message: "Must enter MFA code to impersonate" }
                  ]}>
                  <BaseInput value={mfa} required />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Impersonate
                  </Button>
                </Form.Item>
              </Form>
              <div className="flex items-center justify-center">
                {errors.length > 0 && <FieldErrorMessage message={errors} />}
              </div>
            </ImpersonateSelector>
          </ImpersonateContainer>
        )}
        <div className={`busy-indicator ${!isBusy ? "hidden" : ""}`}>
          <IconSpinner></IconSpinner>
        </div>
      </div>
    </div>
  );
}

const ImpersonateContainer = styled.div`
  .ant-btn {
    border-radius: 6px;
  }
`;

const ImpersonateSelector = styled.div``;
